import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { useWindowMouseDrag } from '~/hooks/useWindowMouseDrag';
import {
    MIN_FOV_MM,
    MovedEdge,
    ViewBoxMm,
    scoutViewBox,
} from '~/domain/services/imageService';
import { ProjectionResult } from '~/domain/services/orthographicProjection';

type DragTarget = 'zTop' | 'zBottom' | 'fovLeft' | 'fovRight' | 'move';

const HANDLE_SIZE = 8;
const HIT_TOLERANCE = 12;
const MIN_Z_GAP_MM = 2;

const Z_COLOR = 'rgba(0, 200, 255, 0.9)';
const EXCLUDED_FILL = 'rgba(0, 0, 0, 0.45)';
/** Hatch over slices that are copies rather than acquired image. */
const REPLICA_HATCH = 'rgba(255, 167, 38, 0.55)';
/** The region's centre marker. */
const CENTER_COLOR = 'rgba(255, 255, 255, 0.9)';
const CENTER_ARM_PX = 6;
/** The slice the output preview is showing. */
const PREVIEW_COLOR = 'rgba(129, 212, 250, 0.9)';

/**
 * An affine map between a world-mm interval and the pixel interval it is
 * drawn into, with its inverse.
 *
 * `flip` runs the world axis the opposite way to the pixel axis, which is
 * what patient Z needs: it increases towards the head, and the canvas
 * y axis increases downward.
 *
 * A degenerate world interval maps everything to the interval's origin
 * rather than dividing by zero; the inverse of a degenerate interval is
 * its own `min`, there being no position to recover.
 */
export function axisMap(
    min: number,
    max: number,
    offsetPx: number,
    extentPx: number,
    flip: boolean
): { toPx: (world: number) => number; toWorld: (px: number) => number } {
    const extent = max - min;
    return {
        toPx: (world: number): number => {
            if (extent <= 0) {
                return offsetPx;
            }
            const t = flip ? max - world : world - min;
            return offsetPx + (t / extent) * extentPx;
        },
        toWorld: (px: number): number => {
            if (extent <= 0 || extentPx <= 0) {
                return min;
            }
            const t = ((px - offsetPx) / extentPx) * extent;
            return flip ? max - t : min + t;
        },
    };
}

/** Draw a small filled triangle handle at a canvas edge. */
function drawHandle(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    direction: 'up' | 'down' | 'left' | 'right',
    color: string
): void {
    const s = HANDLE_SIZE;
    ctx.fillStyle = color;
    ctx.beginPath();
    switch (direction) {
        case 'right':
            ctx.moveTo(x, y - s);
            ctx.lineTo(x + s * 1.5, y);
            ctx.lineTo(x, y + s);
            break;
        case 'left':
            ctx.moveTo(x, y - s);
            ctx.lineTo(x - s * 1.5, y);
            ctx.lineTo(x, y + s);
            break;
        case 'down':
            ctx.moveTo(x - s, y);
            ctx.lineTo(x, y + s * 1.5);
            ctx.lineTo(x + s, y);
            break;
        case 'up':
            ctx.moveTo(x - s, y);
            ctx.lineTo(x, y - s * 1.5);
            ctx.lineTo(x + s, y);
            break;
    }
    ctx.closePath();
    ctx.fill();
}

interface ProjectionCanvasProps {
    projection: ProjectionResult | null;
    label: string;
    axis?: 'coronal' | 'sagittal';
    zRange?: [number, number];
    fovMm?: number;
    /**
     * Centre of the field of view on the axis this view shows, patient mm.
     * Passed rather than assumed to be the image centre: the two in-plane
     * edges move independently, so the box can sit off-centre.
     */
    inPlaneCenterMm?: number;
    /**
     * Slice centre the output preview is showing, patient mm. Marked so
     * the previewed slice can be found on the anatomy it came from.
     */
    previewZMm?: number;
    maxSize?: number;
    /** A drag on one end of the slice range. `moved` names that end. */
    onZRangeChange?: (newRange: [number, number], moved: MovedEdge) => void;
    /**
     * The new [low, high] field-of-view bounds on this view's axis, patient
     * mm. Bounds rather than a width, because dragging one edge holds the
     * other: the caller derives both the width and the centre from them.
     */
    /**
     * A drag on one in-plane edge. `moved` names the edge under the
     * cursor: the other is reported at the value it had when the drag
     * started, which a caller that moves both edges cannot rely on.
     */
    onInPlaneBoundsChange?: (
        bounds: [number, number],
        moved: MovedEdge
    ) => void;
    /**
     * Translate the region by a world-mm delta on this view's two axes,
     * without resizing it.
     */
    onRegionMove?: (inPlaneDeltaMm: number, zDeltaMm: number) => void;
    /**
     * Slices duplicated beyond each end of the range, as world-mm margins.
     * Drawn so the fabricated part of the output is visible on the scout
     * and not only stated in the panel.
     */
    extensionMm?: { inferior: number; superior: number };
}

/**
 * A view box held by value: a recomputed box with identical numbers keeps
 * the previous object, so nothing downstream of it re-runs.
 */
function useStableViewBox(next: ViewBoxMm | null): ViewBoxMm | null {
    const held = useRef<ViewBoxMm | null>(null);
    if (
        next === null ||
        held.current === null ||
        held.current.inPlaneMin !== next.inPlaneMin ||
        held.current.inPlaneMax !== next.inPlaneMax ||
        held.current.zMin !== next.zMin ||
        held.current.zMax !== next.zMax
    ) {
        held.current = next;
    }
    return held.current;
}

/** Wheel step. Zoom only ever tightens the view inside the fitted box. */
const ZOOM_STEP = 1.15;
const MAX_ZOOM = 8;

export default function ProjectionCanvas({
    projection,
    label,
    axis,
    zRange,
    fovMm,
    inPlaneCenterMm,
    previewZMm,
    maxSize = 150,
    onZRangeChange,
    onInPlaneBoundsChange,
    onRegionMove,
    extensionMm,
}: ProjectionCanvasProps) {
    const theme = useTheme();
    const fovColor = theme.palette.secondary.dark;

    const imageCanvasRef = useRef<HTMLCanvasElement>(null);
    const overlayCanvasRef = useRef<HTMLCanvasElement>(null);
    const [cursor, setCursor] = useState('default');
    const [zoom, setZoom] = useState(1);

    const inPlaneAxis = axis === 'sagittal' ? 1 : 0;

    /** The field of view's low and high edge on this axis, patient mm. */
    const fovBounds = useMemo((): [number, number] | null => {
        if (fovMm == null || fovMm <= 0 || inPlaneCenterMm == null) {
            return null;
        }
        return [inPlaneCenterMm - fovMm / 2, inPlaneCenterMm + fovMm / 2];
    }, [fovMm, inPlaneCenterMm]);

    /**
     * What the canvas shows: everything the acquisition covers and
     * everything the region asks for, then tightened by the wheel.
     *
     * Zoom is bounded by the region rather than by a free minimum, so the
     * limits being dragged can never be scrolled off the canvas — the
     * failure the view box exists to prevent.
     */
    const rawViewBox = useMemo((): ViewBoxMm | null => {
        if (!projection) {
            return null;
        }
        return scoutViewBox(
            {
                inPlaneMin: projection.worldMin[inPlaneAxis] as number,
                inPlaneMax: projection.worldMax[inPlaneAxis] as number,
                zMin: projection.worldMin[2] as number,
                zMax: projection.worldMax[2] as number,
            },
            fovBounds,
            zRange
                ? [
                      zRange[0] - (extensionMm?.inferior ?? 0),
                      zRange[1] + (extensionMm?.superior ?? 0),
                  ]
                : null,
            zoom
        );
    }, [projection, inPlaneAxis, zRange, fovBounds, extensionMm, zoom]);

    // Held by VALUE. The box above is a fresh object on every region
    // change, so the layout, the coordinate helpers and both draw effects
    // downstream all invalidated even when the numbers had not moved —
    // which they often have not, since the box is the union of the source
    // and the region.
    const viewBox = useStableViewBox(rawViewBox);

    // The view box, fitted into the square container at its own aspect.
    const layout = useMemo(() => {
        if (!viewBox) {
            return { dw: maxSize, dh: maxSize, ox: 0, oy: 0 };
        }
        const worldW = viewBox.inPlaneMax - viewBox.inPlaneMin;
        const worldH = viewBox.zMax - viewBox.zMin;
        if (worldW <= 0 || worldH <= 0) {
            return { dw: maxSize, dh: maxSize, ox: 0, oy: 0 };
        }
        const scale = Math.min(maxSize / worldW, maxSize / worldH);
        const dw = worldW * scale;
        const dh = worldH * scale;
        return { dw, dh, ox: (maxSize - dw) / 2, oy: (maxSize - dh) / 2 };
    }, [viewBox, maxSize]);

    // --- Coordinate mapping helpers ---------------------------------

    // The two axes differ only in which way they run: Z is drawn
    // increasing UPWARD, so its map is flipped, and the in-plane axis is
    // not. Writing the affine map once is what keeps the forward and
    // inverse of each axis exact inverses of one another — a drag reads
    // through the inverse and draws through the forward, so a discrepancy
    // between them moves the region under the cursor.
    const zMap = useMemo(
        () =>
            axisMap(
                viewBox?.zMin ?? 0,
                viewBox?.zMax ?? 0,
                layout.oy,
                layout.dh,
                true
            ),
        [viewBox, layout]
    );

    const inPlaneMap = useMemo(
        () =>
            axisMap(
                viewBox?.inPlaneMin ?? 0,
                viewBox?.inPlaneMax ?? 0,
                layout.ox,
                layout.dw,
                false
            ),
        [viewBox, layout]
    );

    const worldZToY = useCallback(
        (wz: number): number => (viewBox ? zMap.toPx(wz) : 0),
        [viewBox, zMap]
    );

    const yToWorldZ = useCallback(
        (cy: number): number => (viewBox ? zMap.toWorld(cy) : 0),
        [viewBox, zMap]
    );

    /** Patient mm on this view's in-plane axis -> container x. */
    const worldToX = useCallback(
        (w: number): number => (viewBox ? inPlaneMap.toPx(w) : 0),
        [viewBox, inPlaneMap]
    );

    const xToWorld = useCallback(
        (cx: number): number => (viewBox ? inPlaneMap.toWorld(cx) : 0),
        [viewBox, inPlaneMap]
    );

    // --- Draw MIP image (bottom canvas) ----------------------------

    /**
     * The projection's pixels, uploaded once.
     *
     * `putImageData` of a full MIP is the expensive half of drawing this
     * canvas, and the pixels do not change while the region is dragged —
     * only the rectangle they are drawn into does. Re-uploading them on
     * every drag frame is what made an edge drag flicker.
     */
    const projectionCanvas = useMemo(() => {
        if (!projection) {
            return null;
        }
        const tmp = document.createElement('canvas');
        tmp.width = projection.widthPx;
        tmp.height = projection.heightPx;
        tmp.getContext('2d')?.putImageData(projection.imageData, 0, 0);
        return tmp;
    }, [projection]);

    useEffect(() => {
        const canvas = imageCanvasRef.current;
        if (!canvas || !projection || !projectionCanvas) {
            return;
        }
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return;
        }

        // Assigning to width RESETS the canvas, so it is only done when the
        // size really changed; otherwise every redraw blanked the canvas
        // and the browser could paint that blank frame.
        if (canvas.width !== maxSize || canvas.height !== maxSize) {
            canvas.width = maxSize;
            canvas.height = maxSize;
        } else {
            ctx.clearRect(0, 0, maxSize, maxSize);
        }

        ctx.imageSmoothingEnabled = false;
        // Drawn at the rectangle its OWN world extent occupies in the
        // view, so a view wider than the acquisition leaves the extra
        // space empty rather than stretching the image over it.
        const left = worldToX(projection.worldMin[inPlaneAxis] as number);
        const right = worldToX(projection.worldMax[inPlaneAxis] as number);
        const top = worldZToY(projection.worldMax[2] as number);
        const bottom = worldZToY(projection.worldMin[2] as number);
        ctx.drawImage(projectionCanvas, left, top, right - left, bottom - top);
    }, [
        projection,
        projectionCanvas,
        maxSize,
        inPlaneAxis,
        worldToX,
        worldZToY,
    ]);

    // --- Draw overlay (top canvas) ---------------------------------

    useEffect(() => {
        const canvas = overlayCanvasRef.current;
        if (!canvas) {
            return;
        }
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return;
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        if (!projection) {
            return;
        }

        const hasZ = zRange != null && viewBox != null;
        const hasFov = fovBounds != null && axis != null;

        // Compute line positions in container coordinates
        const zTopY = hasZ ? worldZToY(zRange[1]) : 0;
        const zBotY = hasZ ? worldZToY(zRange[0]) : maxSize;
        const fLX = fovBounds ? worldToX(fovBounds[0]) : 0;
        const fRX = fovBounds ? worldToX(fovBounds[1]) : maxSize;

        // Dark fill on excluded regions
        ctx.fillStyle = EXCLUDED_FILL;
        if (hasZ) {
            // The extension is output, not excluded, so the dimming starts
            // beyond it.
            const outTopY = worldZToY(zRange[1] + (extensionMm?.superior ?? 0));
            const outBotY = worldZToY(zRange[0] - (extensionMm?.inferior ?? 0));
            if (outTopY > 0) {
                ctx.fillRect(0, 0, maxSize, outTopY);
            }
            if (outBotY < maxSize) {
                ctx.fillRect(0, outBotY, maxSize, maxSize - outBotY);
            }
        }
        if (hasFov) {
            const yT = hasZ ? Math.max(0, zTopY) : 0;
            const yB = hasZ ? Math.min(maxSize, zBotY) : maxSize;
            const h = yB - yT;
            if (fLX > 0) {
                ctx.fillRect(0, yT, fLX, h);
            }
            if (fRX < maxSize) {
                ctx.fillRect(fRX, yT, maxSize - fRX, h);
            }
        }

        // The duplicated slices: hatched, so they read as fabricated
        // rather than as more image.
        if (hasZ && extensionMm) {
            const band = (fromZ: number, toZ: number) => {
                const yA = worldZToY(fromZ);
                const yB = worldZToY(toZ);
                const top = Math.min(yA, yB);
                const height = Math.abs(yB - yA);
                if (height <= 0) {
                    return;
                }
                ctx.save();
                ctx.beginPath();
                ctx.rect(0, top, maxSize, height);
                ctx.clip();
                ctx.strokeStyle = REPLICA_HATCH;
                ctx.lineWidth = 1;
                for (let x = -maxSize; x < maxSize * 2; x += 6) {
                    ctx.beginPath();
                    ctx.moveTo(x, top + height);
                    ctx.lineTo(x + height, top);
                    ctx.stroke();
                }
                ctx.restore();
            };
            if (extensionMm.superior > 0) {
                band(zRange[1], zRange[1] + extensionMm.superior);
            }
            if (extensionMm.inferior > 0) {
                band(zRange[0] - extensionMm.inferior, zRange[0]);
            }
        }

        // Z-range lines + handles
        if (hasZ) {
            ctx.strokeStyle = Z_COLOR;
            ctx.lineWidth = 2;

            ctx.beginPath();
            ctx.moveTo(0, zTopY);
            ctx.lineTo(maxSize, zTopY);
            ctx.stroke();

            ctx.beginPath();
            ctx.moveTo(0, zBotY);
            ctx.lineTo(maxSize, zBotY);
            ctx.stroke();

            if (onZRangeChange) {
                drawHandle(ctx, 0, zTopY, 'right', Z_COLOR);
                drawHandle(ctx, maxSize, zTopY, 'left', Z_COLOR);
                drawHandle(ctx, 0, zBotY, 'right', Z_COLOR);
                drawHandle(ctx, maxSize, zBotY, 'left', Z_COLOR);
            }
        }

        // FOV lines + handles
        if (hasFov) {
            ctx.strokeStyle = fovColor;
            ctx.lineWidth = 2;

            ctx.beginPath();
            ctx.moveTo(fLX, 0);
            ctx.lineTo(fLX, maxSize);
            ctx.stroke();

            ctx.beginPath();
            ctx.moveTo(fRX, 0);
            ctx.lineTo(fRX, maxSize);
            ctx.stroke();

            if (onInPlaneBoundsChange) {
                drawHandle(ctx, fLX, 0, 'down', fovColor);
                drawHandle(ctx, fLX, maxSize, 'up', fovColor);
                drawHandle(ctx, fRX, 0, 'down', fovColor);
                drawHandle(ctx, fRX, maxSize, 'up', fovColor);
            }
        }

        // The slice the preview is showing.
        if (previewZMm != null) {
            const y = worldZToY(previewZMm);
            ctx.strokeStyle = PREVIEW_COLOR;
            ctx.lineWidth = 1;
            ctx.setLineDash([4, 4]);
            ctx.beginPath();
            ctx.moveTo(0, y);
            ctx.lineTo(maxSize, y);
            ctx.stroke();
            ctx.setLineDash([]);
        }

        // Centre of the region: what the output's Centre row reports, and
        // what a centre drag moves. Drawn as a cross rather than a dot so
        // it stays legible against the anatomy under it, and short enough
        // not to be mistaken for one of the limit lines.
        if (hasZ && fovBounds) {
            const cx = worldToX((fovBounds[0] + fovBounds[1]) / 2);
            const cy = worldZToY((zRange[0] + zRange[1]) / 2);
            ctx.strokeStyle = CENTER_COLOR;
            ctx.lineWidth = 1.5;
            ctx.beginPath();
            ctx.moveTo(cx - CENTER_ARM_PX, cy);
            ctx.lineTo(cx + CENTER_ARM_PX, cy);
            ctx.moveTo(cx, cy - CENTER_ARM_PX);
            ctx.lineTo(cx, cy + CENTER_ARM_PX);
            ctx.stroke();
        }
    }, [
        previewZMm,
        projection,
        maxSize,
        zRange,
        fovMm,
        axis,
        worldZToY,
        worldToX,
        viewBox,
        fovBounds,
        extensionMm,
        onZRangeChange,
        onInPlaneBoundsChange,
        fovColor,
    ]);

    // --- Mouse helpers ---------------------------------------------

    const getPos = useCallback(
        (e: MouseEvent | React.MouseEvent): { x: number; y: number } => {
            const el = overlayCanvasRef.current;
            if (!el) {
                return { x: 0, y: 0 };
            }
            const rect = el.getBoundingClientRect();
            return {
                x: ((e.clientX - rect.left) / rect.width) * maxSize,
                y: ((e.clientY - rect.top) / rect.height) * maxSize,
            };
        },
        [maxSize]
    );

    const hitTest = useCallback(
        (x: number, y: number): DragTarget | null => {
            if (!projection) {
                return null;
            }

            const hasZ =
                zRange != null &&
                projection.worldMax[2] !== projection.worldMin[2] &&
                onZRangeChange != null;
            const hasFov =
                fovBounds != null &&
                axis != null &&
                onInPlaneBoundsChange != null;

            if (hasZ) {
                const zt = worldZToY(zRange[1]);
                const zb = worldZToY(zRange[0]);
                if (Math.abs(y - zt) < HIT_TOLERANCE) {
                    return 'zTop';
                }
                if (Math.abs(y - zb) < HIT_TOLERANCE) {
                    return 'zBottom';
                }
            }
            if (hasFov) {
                const fl = worldToX(fovBounds[0]);
                const fr = worldToX(fovBounds[1]);
                if (Math.abs(x - fl) < HIT_TOLERANCE) {
                    return 'fovLeft';
                }
                if (Math.abs(x - fr) < HIT_TOLERANCE) {
                    return 'fovRight';
                }
            }

            // Inside the box, clear of every edge: reposition it without
            // resizing. Edges keep priority, so the two gestures do not
            // compete along the border.
            if (onRegionMove && zRange && fovBounds) {
                const withinZ =
                    y > worldZToY(zRange[1]) && y < worldZToY(zRange[0]);
                const withinPlane =
                    x > worldToX(fovBounds[0]) && x < worldToX(fovBounds[1]);
                if (withinZ && withinPlane) {
                    return 'move';
                }
            }
            return null;
        },
        [
            projection,
            zRange,
            fovBounds,
            axis,
            onZRangeChange,
            onInPlaneBoundsChange,
            onRegionMove,
            worldZToY,
            worldToX,
        ]
    );

    // --- Mouse event handlers --------------------------------------

    const handleHover = useCallback(
        (e: React.MouseEvent) => {
            const p = getPos(e);
            const t = hitTest(p.x, p.y);
            if (t === 'zTop' || t === 'zBottom') {
                setCursor('ns-resize');
            } else if (t === 'fovLeft' || t === 'fovRight') {
                setCursor('ew-resize');
            } else if (t === 'move') {
                setCursor('move');
            } else {
                setCursor('default');
            }
        },
        [getPos, hitTest]
    );

    // The drag in progress, captured at mousedown: which handle, and the
    // ends it must hold still. Held in a ref so the window listeners stay
    // stable while the values they read stay current.
    const dragRef = useRef<{
        target: DragTarget;
        fixedZMin: number;
        fixedZMax: number;
        fixedFovLow: number;
        fixedFovHigh: number;
        lastInPlaneMm: number;
        lastZMm: number;
    } | null>(null);

    const onDragMove = useCallback(
        (e: MouseEvent) => {
            const drag = dragRef.current;
            if (!drag || !projection) {
                return;
            }
            const mp = getPos(e);
            const wZMin = projection.worldMin[2] as number;
            const wZMax = projection.worldMax[2] as number;

            switch (drag.target) {
                case 'zTop': {
                    const z = Math.min(
                        wZMax,
                        Math.max(drag.fixedZMin + MIN_Z_GAP_MM, yToWorldZ(mp.y))
                    );
                    onZRangeChange?.([drag.fixedZMin, z], 'high');
                    break;
                }
                case 'zBottom': {
                    const z = Math.max(
                        wZMin,
                        Math.min(drag.fixedZMax - MIN_Z_GAP_MM, yToWorldZ(mp.y))
                    );
                    onZRangeChange?.([z, drag.fixedZMax], 'low');
                    break;
                }
                // The edge under the cursor is named; the opposite one is
                // reported where it was at mousedown. What the region does
                // with that — hold the opposite edge, or mirror about the
                // centre — is the caller's to decide.
                case 'fovLeft': {
                    const low = Math.min(
                        drag.fixedFovHigh - MIN_FOV_MM,
                        xToWorld(mp.x)
                    );
                    onInPlaneBoundsChange?.([low, drag.fixedFovHigh], 'low');
                    break;
                }
                case 'fovRight': {
                    const high = Math.max(
                        drag.fixedFovLow + MIN_FOV_MM,
                        xToWorld(mp.x)
                    );
                    onInPlaneBoundsChange?.([drag.fixedFovLow, high], 'high');
                    break;
                }
                // Reported as a delta from the last frame, so the region
                // follows the pointer without the caller needing to know
                // where the grab started.
                case 'move': {
                    const inPlane = xToWorld(mp.x);
                    const z = yToWorldZ(mp.y);
                    onRegionMove?.(
                        inPlane - drag.lastInPlaneMm,
                        z - drag.lastZMm
                    );
                    drag.lastInPlaneMm = inPlane;
                    drag.lastZMm = z;
                    break;
                }
            }
        },
        [
            projection,
            getPos,
            yToWorldZ,
            xToWorld,
            onZRangeChange,
            onInPlaneBoundsChange,
            onRegionMove,
        ]
    );

    const onDragEnd = useCallback(() => {
        dragRef.current = null;
    }, []);

    const startDrag = useWindowMouseDrag(onDragMove, onDragEnd);

    const handleMouseDown = useCallback(
        (e: React.MouseEvent) => {
            const p = getPos(e);
            const target = hitTest(p.x, p.y);
            if (!target || !projection) {
                return;
            }
            dragRef.current = {
                target,
                fixedZMin: zRange ? zRange[0] : 0,
                fixedZMax: zRange ? zRange[1] : 0,
                fixedFovLow: fovBounds ? fovBounds[0] : 0,
                fixedFovHigh: fovBounds ? fovBounds[1] : 0,
                lastInPlaneMm: xToWorld(p.x),
                lastZMm: yToWorldZ(p.y),
            };
            startDrag(e);
        },
        [
            getPos,
            hitTest,
            projection,
            zRange,
            fovBounds,
            startDrag,
            xToWorld,
            yToWorldZ,
        ]
    );

    // Wheel tightens the view; the fitted box is the floor, so a limit can
    // never be scrolled out of sight. Native non-passive listener for the
    // reason `NumberInput` gives.
    useEffect(() => {
        const host = overlayCanvasRef.current;
        if (!host) {
            return;
        }
        const onWheel = (event: WheelEvent) => {
            if (event.deltaY === 0) {
                return;
            }
            event.preventDefault();
            setZoom((current) =>
                Math.min(
                    MAX_ZOOM,
                    Math.max(
                        1,
                        current * (event.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP)
                    )
                )
            );
        };
        host.addEventListener('wheel', onWheel, { passive: false });
        return () => host.removeEventListener('wheel', onWheel);
    }, []);

    return (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={0.5}
        >
            <Box
                sx={{
                    position: 'relative',
                    width: maxSize,
                    height: maxSize,
                    flexShrink: 0,
                    backgroundColor: '#000',
                    borderRadius: 1,
                    overflow: 'hidden',
                }}
            >
                <canvas
                    ref={imageCanvasRef}
                    width={maxSize}
                    height={maxSize}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        imageRendering: 'pixelated',
                        pointerEvents: 'none',
                    }}
                />
                <canvas
                    ref={overlayCanvasRef}
                    width={maxSize}
                    height={maxSize}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        cursor,
                    }}
                    onMouseMove={handleHover}
                    onMouseDown={handleMouseDown}
                    onMouseLeave={() => setCursor('default')}
                />
            </Box>
            <Typography variant="caption" color="text.secondary">
                {label}
            </Typography>
        </Box>
    );
}
